import React, { useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from '../components/Header';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/TopicDetails.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faFilter, faFilePdf, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import { useTranslation } from "react-i18next";
import api from '../api/axios';
import { openPdfTab, sanitizeFilenameForUrl } from '../utils/pdfTab';
import UserAvatar from '../components/UserAvatar';

function TopicDetails() {
    const navigate = useNavigate();
    const { id, idt } = useParams();
    const { municipalityId } = useParams();
    const [topicDetails, setTopicDetails] = useState(null);
    const [loading, setLoading] = useState(true); // Add loading state
    const [showVotes, setShowVotes] = useState(null);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        const fetchTopicDetails = async () => {
            setLoading(true);

            try {
                const { data } = await api.get(`/api/topics/details/${idt}`);

                setTopicDetails(data);

                const shouldShowVotes =
                    data.status === "ACTIVE" || data.status === "FINISHED";
                setShowVotes(shouldShowVotes);

            } catch (error) {
                console.error("Failed to fetch topic details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (idt) {
            fetchTopicDetails();
        }
    }, [idt]);

    const handlePdfFetch = async (pdfId) => {
        const newTab = openPdfTab();
        try {
            const { data } = await api.get(`/api/topics/pdf/${pdfId}/name`);
            const token = localStorage.getItem('jwtToken');
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const safeName = sanitizeFilenameForUrl(data.fileName);
            if (newTab && !newTab.closed) {
                newTab.location.href = `${baseUrl}/api/topics/pdf/${pdfId}/${encodeURIComponent(safeName)}?token=${encodeURIComponent(token)}`;
            }
        } catch (error) {
            console.error('Error fetching PDF:', error);
            if (newTab && !newTab.closed) newTab.close();
        }
    };

    const handlePdfDownload = async (pdfId) => {
        if (isPdfDownloading) return;
        setIsPdfDownloading(true);
        try {
            const { data: nameData } = await api.get(`/api/topics/pdf/${pdfId}/name`);
            const fileName = nameData.fileName || 'document.pdf';
            const response = await api.get(`/api/topics/pdf/${pdfId}/${encodeURIComponent(fileName)}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleBackButtonClick = () => {
        // Remove the scroll position from sessionStorage
        sessionStorage.removeItem('scrollPosition');
        console.log("Scroll position removed");

        // Navigate to the desired URL
        navigate(`/municipalities/${municipalityId}/sessions/${id}/topics#topic-${idt}`);
    };

    const getTitleFontSize = (title) => {
        const len = title?.length || 0;
        if (len > 400) return '16px';
        if (len > 250) return '18px';
        if (len > 150) return '22px';
        if (len > 80)  return '26px';
        return null; // use CSS default (29px)
    };

    return (
        <div className="topic-details-container">
            <HelmetProvider>
                <Helmet>
                    <title>
                    {topicDetails && (topicDetails.status === 'ACTIVE' || topicDetails.status === 'FINISHED')
                        ? t("topicsDetails.detailedResults")
                        : t("topicsDetails.details")}
                    </title>
                </Helmet>
            </HelmetProvider>
            <Header />
            <main className='topic-details-body-container'>
                {loading ? (
                    <div className="loading-spinner">
                        <img src={`${process.env.PUBLIC_URL}/images/loading.svg`} alt="Loading..." />
                    </div>
                ) : (
                    topicDetails && (
                    <>
                        <div className='back-button-detailed-results-container'>
                            <button onClick={handleBackButtonClick} className="back-button">
                                <span className="back-icon">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </span>
                                <span className="back-text">
                                    {t("common.back")}
                                </span>
                            </button>
                        </div>
                        <div className="detailed-result-header">
                            <h1 className="topicDetails-header-title">
                                {showVotes ? t("topicsDetails.detailedResults") : t("topicsDetails.details")}
                            </h1>
                        </div>
                      <div>
                        <div>
                            <p className='detail-title' style={getTitleFontSize(topicDetails.title) ? { fontSize: getTitleFontSize(topicDetails.title) } : undefined}>{topicDetails.title}</p>
                        </div>
                        {topicDetails && topicDetails.status === "CREATED" && (
                            <div>
                                <p><strong>{t("topicsDetails.notStartedMessage")}</strong></p>
                            </div>
                        )}
                        {topicDetails.pdfFileId != null && (
                            <div className="pdf-actions-container">
                                <button
                                    className="pdf-action-btn pdf-action-btn--view"
                                    onClick={() => handlePdfFetch(topicDetails.pdfFileId)}
                                >
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    {t("topicsDetails.viewDocument")}
                                </button>
                                <div className="pdf-actions-divider" />
                                <button
                                    className="pdf-action-btn pdf-action-btn--download"
                                    onClick={() => handlePdfDownload(topicDetails.pdfFileId)}
                                >
                                    <FontAwesomeIcon icon={isPdfDownloading ? faSpinner : faDownload} spin={isPdfDownloading} />
                                    {t("topicsDetails.downloadDocument")}
                                </button>
                            </div>
                        )}
                        {showVotes && (
                            <div className="vote-summary-container">
                                <div className="vote-summary-grid">
                                    <span className="topic-detail-yes-sum">{t("topicsDetails.yes")}: {topicDetails.yesUsers.length}</span>
                                    <span className="topic-detail-no-sum">{t("topicsDetails.no")}: {topicDetails.noUsers.length}</span>
                                    <span className="topic-detail-abstained-sum">{t("topicsDetails.abstained")}: {topicDetails.abstainedUsers.length}</span>
                                    <span className="topic-detail-cant-vote-sum">{t("topicsDetails.cantVote")}: {topicDetails.cantVoteUsers.length}</span>
                                    <span className="topic-detail-havent-vote-sum">{t("topicsDetails.notVoted")}: {topicDetails.haventVoteUsers.length}</span>
                                    <span className="topic-detail-absent-sum">{t("topicsDetails.absent")}: {topicDetails.absentVoteUsers.length}</span>
                                </div>
                            </div>
                        )}
        {showVotes && (
         <div  className="table-wrapper">
            <table className="details-table">
                <thead>
                    <tr>
                        <th className="text-center details-table-th">{t("topicsDetails.image")}</th>
                        <th className="text-center details-table-th">{t("topicsDetails.fullName")}</th>
                        <th className="text-center details-table-th">{t("topicsDetails.vote")} <FontAwesomeIcon icon={faFilter} /></th>
                    </tr>
                </thead>
                <tbody>
                {[...(topicDetails.yesUsers || [])].map((user, index) => (
                        <tr key={`yes-${index}`}>
                            <td>
                                <UserAvatar username={user.username} name={user.name} surname={user.surname} className="details-image" />
                            </td>
                            <td>{user.name} {user.surname}</td>
                            <td><span className="topic-detail-yes">{t("topicsDetails.yes")}</span></td>
                        </tr>
                    ))}

                    {[...(topicDetails.noUsers || [])].map((user, index) => (
                        <tr key={`no-${index}`}>
                            <td>
                                <UserAvatar username={user.username} name={user.name} surname={user.surname} className="details-image" />
                            </td>
                            <td>{user.name} {user.surname}</td>
                            <td><span className="topic-detail-no">{t("topicsDetails.no")}</span></td>
                        </tr>
                    ))}

                    {[...(topicDetails.abstainedUsers || [])].map((user, index) => (
                        <tr key={`abstained-${index}`}>
                            <td>
                                <UserAvatar username={user.username} name={user.name} surname={user.surname} className="details-image" />
                            </td>
                            <td>{user.name} {user.surname}</td>
                            <td><span className="topic-detail-abstained">{t("topicsDetails.abstained")}</span></td>
                        </tr>
                    ))}

                    {[...(topicDetails.cantVoteUsers || [])].map((user, index) => (
                        <tr key={`cantVote-${index}`}>
                            <td>
                                <UserAvatar username={user.username} name={user.name} surname={user.surname} className="details-image" />
                            </td>
                            <td>{user.name} {user.surname}</td>
                            <td><span className="topic-detail-cant-vote">{t("topicsDetails.cantVote")}</span></td>
                        </tr>
                    ))}

                    {[...(topicDetails.haventVoteUsers || [])].map((user, index) => (
                        <tr key={`notVoted-${index}`}>
                            <td>
                                <UserAvatar username={user.username} name={user.name} surname={user.surname} className="details-image" />
                            </td>
                            <td>{user.name} {user.surname}</td>
                            <td><span className="topic-detail-havent-vote">{t("topicsDetails.notVoted")}</span></td>
                        </tr>
                    ))}

                    {[...(topicDetails.absentVoteUsers || [])].map((user, index) => (
                        <tr key={`absent-${index}`}>
                            <td>
                                <UserAvatar username={user.username} name={user.name} surname={user.surname} className="details-image" />
                            </td>
                            <td>{user.name} {user.surname}</td>
                            <td><span className="topic-detail-absent">{t("topicsDetails.absent")}</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
         </div>
        )}

    </div>
                    </>
                    )
                )}
            </main>
            {!loading && <Footer />}
        </div>
    );
}

export default TopicDetails;
